//import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import useUser from "@/lib/queries/user/useUser"; //User
import { useEffect, useState } from "react";
import Loading from "@/components/Loading";
import { useStytchSession } from "@stytch/react";
import "./meter.css";
import { Button } from "./ui/button";
import { MagicWand } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { CHECKOUT } from "@/lib/links";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

function CreditMeter({ max, current }: { max: number; current: number }) {
  const { t } = useTranslation(["settings", "common"]);

  return (
    <div
      className={cn(
        "flex flex-col w-full px-5 py-5 rounded-md",
        current === 0
          ? "bg-destructive/15 text-destructive"
          : "bg-meter-optimum/15"
      )}
    >
      {current === max && (
        <label htmlFor="trial-uses" className="text-2xl">
          {t("subscriber.credits.remaining", { current })}
        </label>
      )}
      {current < max && current >= 0 && (
        <label htmlFor="trial-uses" className="text-2xl">
          {t("subscriber.credits.remainingWithMax", { current, max })}
        </label>
      )}

      {current > 0 && (
        <meter id="trial-uses" min={0} max={max} value={current} low={5} />
      )}
    </div>
  );
}

function SubscriberDetails() {
  const { t } = useTranslation(["settings", "common"]);
  const stytchSession = useStytchSession();
  const { data: user, isLoading: isUserLoading } = useUser({
    enabled: stytchSession.session !== null,
  });

  const [isDataLoading, setIsDataLoading] = useState(false);

  useEffect(() => {
    if (isUserLoading) {
      setIsDataLoading(true);
    } else {
      setIsDataLoading(false);
    }
  }, [isUserLoading, isDataLoading]);

  if (isDataLoading) {
    return <Loading />;
  }

  if (!user) {
    return (
      <Card className="bg-card w-fit h-fit border-destructive/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl text-destructive">
            <MagicWand className="w-6 h-6" weight="bold" />
            {t("subscriber.error.title")}
          </CardTitle>
          <CardDescription className="text-base">
            {t("subscriber.error.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-md bg-muted/50">
            <p className="text-sm text-muted-foreground">
              {t("subscriber.error.help")}
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          <Button
            onClick={() => window.location.reload()}
            className="w-full font-bold rounded-full"
          >
            {t("subscriber.error.refreshButton")}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            {t("subscriber.error.supportMessage")}
          </p>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="bg-card w-fit h-fit">
      <CardHeader>
        <CardTitle className="text-4xl text-primary">
          {t("subscriber.trial.title")}
        </CardTitle>
        <CardDescription className="mt-5 text-2xl font-semibold text-foreground">
          {t("subscriber.trial.subtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <CreditMeter
          current={user?.creditsRemaining ?? 0}
          max={user?.creditsMax ?? 0}
        />
      </CardContent>
      <CardFooter>
        {(user.trialActive || !user.trialActive || user.trialUsed) &&
          !user.subscribed && (
            <Link to={CHECKOUT} className="w-full font-bold rounded-full">
              <Button className="w-full font-bold rounded-full">
                <MagicWand
                  className="w-6 h-6 mr-2 font-bold whitespace-normal"
                  weight="bold"
                />
                {t("subscriber.subscribe.button")}
              </Button>
            </Link>
          )}
      </CardFooter>
    </Card>
  );
}

export default SubscriberDetails;
